import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ExcelJS from "exceljs";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { getAdminData, checkIsAdmin } from "@/lib/admin.functions";
import {
  adminCreateCafe,
  adminUpdateCafe,
  adminDeleteCafe,
  adminCreateClub,
  adminUpdateClub,
  adminDeleteClub,
  adminCreateUser,
  adminUpdateProfile,
  adminDeleteUser,
  adminCreateEvent,
  adminUpdateEvent,
  adminDeleteEvent,
  adminSetBusinessStatus,
} from "@/lib/admin-mutations.functions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Admin — Brulo" }] }),
  component: AdminPage,
});

function AdminPage() {
  const { user, loading } = useAuth();
  const fetchAdminData = useServerFn(getAdminData);
  const fetchIsAdmin = useServerFn(checkIsAdmin);

  const { data: adminCheck, isLoading: checkingAdmin } = useQuery({
    queryKey: ["is-admin", user?.id],
    queryFn: () => fetchIsAdmin(),
    enabled: !!user,
  });
  const isAdmin = !!adminCheck?.isAdmin;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-data"],
    queryFn: () => fetchAdminData(),
    enabled: isAdmin,
  });

  if (loading || checkingAdmin) return <div className="p-8 text-muted-foreground">Cargando...</div>;
  if (!isAdmin) return <Navigate to="/dashboard" />;

  const today = new Date().toISOString().slice(0, 10);
  const exportXlsx = async (rows: any[], tabName: string) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(tabName);
    if (rows.length > 0) {
      const headers = Object.keys(rows[0]);
      ws.columns = headers.map((h) => ({ header: h, key: h }));
      rows.forEach((r) => ws.addRow(r));
    }
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Brulo_${tabName}_${today}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pendingCount =
    ((data?.cafes ?? []).filter((c: any) => c.status === "pending").length as number) +
    ((data?.clubs ?? []).filter((c: any) => c.status === "pending").length as number);

  return (
    <main className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8">
      <h1 className="font-display text-2xl sm:text-3xl mb-6">Panel de administración</h1>
      {pendingCount > 0 && (
        <p className="mb-4 text-sm text-muted-foreground">
          Tienes{" "}
          <span className="font-semibold text-primary">{pendingCount}</span> negocio(s)
          pendiente(s) de aprobación.
        </p>
      )}
      {isLoading || !data ? (
        <p className="text-muted-foreground">Cargando datos...</p>
      ) : (
        <Tabs defaultValue="resumen">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="resumen">Resumen</TabsTrigger>
            <TabsTrigger value="pendientes" className="gap-2">
              Pendientes
              {pendingCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="usuarios">Usuarios</TabsTrigger>
            <TabsTrigger value="clubs">Clubs</TabsTrigger>
            <TabsTrigger value="locales">Locales</TabsTrigger>
            <TabsTrigger value="eventos">Eventos</TabsTrigger>
            <TabsTrigger value="invitados">Cafés invitados</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen">
            <ResumenTab metrics={data.metrics} />
          </TabsContent>
          <TabsContent value="pendientes">
            <PendientesTab cafes={data.cafes} clubs={data.clubs} />
          </TabsContent>
          <TabsContent value="usuarios">
            <UsuariosTab profiles={data.profiles} onExport={exportXlsx} />
          </TabsContent>
          <TabsContent value="clubs">
            <ClubsTab clubs={data.clubs} profiles={data.profiles} onExport={exportXlsx} />
          </TabsContent>
          <TabsContent value="locales">
            <LocalesTab cafes={data.cafes} profiles={data.profiles} onExport={exportXlsx} />
          </TabsContent>
          <TabsContent value="eventos">
            <EventosTab
              events={data.events}
              clubs={data.clubs}
              cafes={data.cafes}
              profiles={data.profiles}
              onExport={exportXlsx}
            />
          </TabsContent>
          <TabsContent value="invitados">
            <InvitadosTab invitados={(data as any).cafesInvitados ?? []} onExport={exportXlsx} />
          </TabsContent>
        </Tabs>
      )}
    </main>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-6">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-3xl font-display mt-2">{value}</div>
    </Card>
  );
}

function ResumenTab({ metrics }: { metrics: any }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <Metric label="Corredores" value={metrics.runners} />
      <Metric label="Organizadores" value={metrics.organizers} />
      <Metric label="Locales" value={metrics.cafes} />
      <Metric label="Eventos" value={metrics.events} />
      <Metric label="Inscripciones" value={metrics.inscripciones} />
      <Metric label="Eventos esta semana" value={metrics.eventsThisWeek} />
    </div>
  );
}

function ExportBtn({ rows, name }: { rows: any[]; name: string }) {
  // Used inline next to "Nuevo" buttons
  return null as any;
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["admin-data"] });
}

/* ------------------------------------------------------------------ */
/* USUARIOS                                                            */
/* ------------------------------------------------------------------ */
function UsuariosTab({
  profiles,
  onExport,
}: {
  profiles: any[];
  onExport: (rows: any[], name: string) => void;
}) {
  const [role, setRole] = useState<string>("todos");
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<any | null>(null);
  const refresh = useRefresh();

  const filtered = useMemo(
    () => (role === "todos" ? profiles : profiles.filter((p) => p.role === role)),
    [profiles, role],
  );
  const exportRows = filtered.map((p) => ({
    Nombre: p.full_name ?? "",
    Email: p.email ?? "",
    Rol: p.role,
    Ciudad: p.city ?? "",
    Telefono: p.phone ?? "",
    Registro: p.created_at,
  }));

  const updateFn = useServerFn(adminUpdateProfile);
  const deleteFn = useServerFn(adminDeleteUser);
  const createFn = useServerFn(adminCreateUser);

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los roles</SelectItem>
            <SelectItem value="runner">Corredores</SelectItem>
            <SelectItem value="organizer">Organizadores</SelectItem>
            <SelectItem value="cafe">Locales</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button onClick={() => setCreating(true)} size="sm">
            <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
          </Button>
          <Button onClick={() => onExport(exportRows, "Usuarios")} size="sm" variant="outline">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
        </div>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Ciudad</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead className="w-32">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.full_name ?? ""}</TableCell>
                <TableCell>{p.email ?? ""}</TableCell>
                <TableCell>{p.role}</TableCell>
                <TableCell>{p.city ?? ""}</TableCell>
                <TableCell>{p.phone ?? ""}</TableCell>
                <TableCell>
                  <RowActions onEdit={() => setEditing(p)} onDelete={() => setDeleting(p)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <UserFormDialog
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (vals) => {
            await updateFn({
              data: {
                id: editing.id,
                data: {
                  full_name: vals.full_name,
                  phone: vals.phone || null,
                  city: vals.city || null,
                  estado: vals.estado || null,
                  role: vals.role as any,
                  disciplinas: vals.disciplinas,
                },
              },
            });
          }}
          onDone={refresh}
        />
      )}
      {creating && (
        <UserFormDialog
          mode="create"
          initial={{}}
          onClose={() => setCreating(false)}
          onSubmit={async (vals) => {
            await createFn({
              data: {
                email: vals.email!,
                full_name: vals.full_name!,
                phone: vals.phone || null,
                city: vals.city || null,
                estado: vals.estado || null,
                role: vals.role as any,
                disciplinas: vals.disciplinas,
              },
            });
          }}
          onDone={refresh}
        />
      )}
      <ConfirmDelete
        open={!!deleting}
        title={`Eliminar usuario ${deleting?.full_name ?? ""}`}
        description="Se eliminará la cuenta y todos sus datos asociados."
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await deleteFn({ data: { id: deleting.id } });
            toast.success("Usuario eliminado");
            refresh();
          } catch (e: any) {
            toast.error(e?.message ?? "Error al eliminar");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </div>
  );
}

function UserFormDialog({
  mode,
  initial,
  onClose,
  onSubmit,
  onDone,
}: {
  mode: "create" | "edit";
  initial: any;
  onClose: () => void;
  onSubmit: (vals: any) => Promise<void>;
  onDone: () => void;
}) {
  const [vals, setVals] = useState({
    email: initial.email ?? "",
    full_name: initial.full_name ?? "",
    phone: initial.phone ?? "",
    city: initial.city ?? "",
    estado: initial.estado ?? "",
    role: initial.role ?? "runner",
    disciplinas: (initial.disciplinas ?? []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({
        ...vals,
        disciplinas: vals.disciplinas
          ? vals.disciplinas.split(",").map((s: string) => s.trim()).filter(Boolean)
          : null,
      });
      toast.success(mode === "create" ? "Usuario creado" : "Usuario actualizado");
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuevo usuario" : "Editar usuario"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {mode === "create" && (
            <Field className="col-span-2" label="Email">
              <Input value={vals.email} onChange={(e) => setVals({ ...vals, email: e.target.value })} />
            </Field>
          )}
          <Field className="col-span-2" label="Nombre completo">
            <Input value={vals.full_name} onChange={(e) => setVals({ ...vals, full_name: e.target.value })} />
          </Field>
          <Field label="Teléfono">
            <Input value={vals.phone} onChange={(e) => setVals({ ...vals, phone: e.target.value })} />
          </Field>
          <Field label="Ciudad">
            <Input value={vals.city} onChange={(e) => setVals({ ...vals, city: e.target.value })} />
          </Field>
          <Field label="Estado">
            <Input value={vals.estado} onChange={(e) => setVals({ ...vals, estado: e.target.value })} />
          </Field>
          <Field label="Rol">
            <Select value={vals.role} onValueChange={(v) => setVals({ ...vals, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="runner">Atleta</SelectItem>
                <SelectItem value="organizer">Organizador</SelectItem>
                <SelectItem value="cafe">Local</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field className="col-span-2" label="Disciplinas (separadas por coma)">
            <Input value={vals.disciplinas} onChange={(e) => setVals({ ...vals, disciplinas: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* CLUBS                                                               */
/* ------------------------------------------------------------------ */
function ClubsTab({
  clubs,
  profiles,
  onExport,
}: {
  clubs: any[];
  profiles: any[];
  onExport: (rows: any[], name: string) => void;
}) {
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<any | null>(null);
  const refresh = useRefresh();
  const updateFn = useServerFn(adminUpdateClub);
  const deleteFn = useServerFn(adminDeleteClub);
  const createFn = useServerFn(adminCreateClub);

  const exportRows = clubs.map((c) => ({
    Nombre: c.name,
    Tipo: c.type,
    Ciudad: c.city ?? "",
    Organizador: c.organizer_name,
    Eventos: c.events_count,
  }));

  return (
    <div>
      <div className="flex justify-end gap-2 mb-3">
        <Button onClick={() => setCreating(true)} size="sm">
          <Plus className="w-4 h-4 mr-2" /> Nuevo club
        </Button>
        <Button onClick={() => onExport(exportRows, "Clubs")} size="sm" variant="outline">
          <Download className="w-4 h-4 mr-2" /> Excel
        </Button>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Ciudad</TableHead>
              <TableHead>Organizador</TableHead>
              <TableHead>Eventos</TableHead>
              <TableHead className="w-32">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clubs.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.type}</TableCell>
                <TableCell>{c.city ?? ""}</TableCell>
                <TableCell>{c.organizer_name}</TableCell>
                <TableCell>{c.events_count}</TableCell>
                <TableCell>
                  <RowActions onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <ClubFormDialog
          mode="edit"
          initial={editing}
          profiles={profiles}
          onClose={() => setEditing(null)}
          onSubmit={async (vals) => {
            await updateFn({ data: { id: editing.id, data: vals } });
          }}
          onDone={refresh}
        />
      )}
      {creating && (
        <ClubFormDialog
          mode="create"
          initial={{}}
          profiles={profiles}
          onClose={() => setCreating(false)}
          onSubmit={async (vals) => {
            await createFn({ data: vals });
          }}
          onDone={refresh}
        />
      )}
      <ConfirmDelete
        open={!!deleting}
        title={`Eliminar club ${deleting?.name ?? ""}`}
        description="Esta acción no se puede deshacer."
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await deleteFn({ data: { id: deleting.id } });
            toast.success("Club eliminado");
            refresh();
          } catch (e: any) {
            toast.error(e?.message ?? "Error al eliminar");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </div>
  );
}

function ClubFormDialog({
  mode,
  initial,
  profiles,
  onClose,
  onSubmit,
  onDone,
}: {
  mode: "create" | "edit";
  initial: any;
  profiles: any[];
  onClose: () => void;
  onSubmit: (vals: any) => Promise<void>;
  onDone: () => void;
}) {
  const organizers = profiles.filter((p) => p.role === "organizer");
  const [vals, setVals] = useState({
    name: initial.name ?? "",
    type: initial.type ?? "run",
    city: initial.city ?? "",
    estado: initial.estado ?? "",
    description: initial.description ?? "",
    instagram: initial.instagram ?? "",
    member_count_range: initial.member_count_range ?? "11-30",
    phone: initial.phone ?? "",
    contact_email: initial.contact_email ?? "",
    owner_id: initial.owner_id ?? organizers[0]?.id ?? "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const payload: any = { ...vals };
      if (mode === "edit") delete payload.owner_id;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.name = vals.name;
      payload.type = vals.type;
      await onSubmit(payload);
      toast.success(mode === "create" ? "Club creado" : "Club actualizado");
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuevo club" : "Editar club"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field className="col-span-2" label="Nombre">
            <Input value={vals.name} onChange={(e) => setVals({ ...vals, name: e.target.value })} />
          </Field>
          <Field label="Tipo">
            <Select value={vals.type} onValueChange={(v) => setVals({ ...vals, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="run">Running</SelectItem>
                <SelectItem value="bike">Ciclismo</SelectItem>
                <SelectItem value="trail">Trail</SelectItem>
                <SelectItem value="multi">Multidisciplina</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Miembros">
            <Select value={vals.member_count_range} onValueChange={(v) => setVals({ ...vals, member_count_range: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1-10">1-10</SelectItem>
                <SelectItem value="11-30">11-30</SelectItem>
                <SelectItem value="31-100">31-100</SelectItem>
                <SelectItem value="100+">100+</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Ciudad">
            <Input value={vals.city} onChange={(e) => setVals({ ...vals, city: e.target.value })} />
          </Field>
          <Field label="Estado">
            <Input value={vals.estado} onChange={(e) => setVals({ ...vals, estado: e.target.value })} />
          </Field>
          <Field className="col-span-2" label="Descripción (max 120)">
            <Textarea
              maxLength={120}
              value={vals.description}
              onChange={(e) => setVals({ ...vals, description: e.target.value })}
            />
          </Field>
          <Field label="Instagram">
            <Input value={vals.instagram} onChange={(e) => setVals({ ...vals, instagram: e.target.value })} />
          </Field>
          <Field label="Teléfono">
            <Input value={vals.phone} onChange={(e) => setVals({ ...vals, phone: e.target.value })} />
          </Field>
          <Field className="col-span-2" label="Email de contacto">
            <Input value={vals.contact_email} onChange={(e) => setVals({ ...vals, contact_email: e.target.value })} />
          </Field>
          {mode === "create" && (
            <Field className="col-span-2" label="Organizador (owner)">
              <Select value={vals.owner_id} onValueChange={(v) => setVals({ ...vals, owner_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {organizers.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.full_name ?? o.email ?? o.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* LOCALES                                                             */
/* ------------------------------------------------------------------ */
function LocalesTab({
  cafes,
  profiles,
  onExport,
}: {
  cafes: any[];
  profiles: any[];
  onExport: (rows: any[], name: string) => void;
}) {
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<any | null>(null);
  const refresh = useRefresh();
  const updateFn = useServerFn(adminUpdateCafe);
  const deleteFn = useServerFn(adminDeleteCafe);
  const createFn = useServerFn(adminCreateCafe);

  const exportRows = cafes.map((c) => ({
    Nombre: c.name,
    Direccion: c.location,
    Ciudad: c.city ?? "",
    Telefono: c.phone ?? "",
    Email: c.owner_email ?? "",
    Eventos: c.events_count,
  }));

  return (
    <div>
      <div className="flex justify-end gap-2 mb-3">
        <Button onClick={() => setCreating(true)} size="sm">
          <Plus className="w-4 h-4 mr-2" /> Nuevo local
        </Button>
        <Button onClick={() => onExport(exportRows, "Locales")} size="sm" variant="outline">
          <Download className="w-4 h-4 mr-2" /> Excel
        </Button>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Dirección</TableHead>
              <TableHead>Ciudad</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Eventos</TableHead>
              <TableHead className="w-32">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cafes.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.location}</TableCell>
                <TableCell>{c.city ?? ""}</TableCell>
                <TableCell>{c.phone ?? ""}</TableCell>
                <TableCell>{c.events_count}</TableCell>
                <TableCell>
                  <RowActions onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <CafeFormDialog
          mode="edit"
          initial={editing}
          profiles={profiles}
          onClose={() => setEditing(null)}
          onSubmit={async (vals) => {
            await updateFn({ data: { id: editing.id, data: vals } });
          }}
          onDone={refresh}
        />
      )}
      {creating && (
        <CafeFormDialog
          mode="create"
          initial={{}}
          profiles={profiles}
          onClose={() => setCreating(false)}
          onSubmit={async (vals) => {
            await createFn({ data: vals });
          }}
          onDone={refresh}
        />
      )}
      <ConfirmDelete
        open={!!deleting}
        title={`Eliminar local ${deleting?.name ?? ""}`}
        description="Esta acción no se puede deshacer."
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await deleteFn({ data: { id: deleting.id } });
            toast.success("Local eliminado");
            refresh();
          } catch (e: any) {
            toast.error(e?.message ?? "Error al eliminar");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </div>
  );
}

function CafeFormDialog({
  mode,
  initial,
  profiles,
  onClose,
  onSubmit,
  onDone,
}: {
  mode: "create" | "edit";
  initial: any;
  profiles: any[];
  onClose: () => void;
  onSubmit: (vals: any) => Promise<void>;
  onDone: () => void;
}) {
  const cafeOwners = profiles.filter((p) => p.role === "cafe");
  const [vals, setVals] = useState({
    name: initial.name ?? "",
    location: initial.location ?? "",
    city: initial.city ?? "",
    estado: initial.estado ?? "",
    codigo_postal: initial.codigo_postal ?? "",
    phone: initial.phone ?? "",
    description: initial.description ?? "",
    member_discount: initial.member_discount ?? "",
    opens_at: initial.opens_at ?? "",
    closes_at: initial.closes_at ?? "",
    hours: initial.hours ?? "",
    google_maps_url: initial.google_maps_url ?? "",
    owner_id: initial.owner_id ?? cafeOwners[0]?.id ?? "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const payload: any = { ...vals };
      if (mode === "edit") delete payload.owner_id;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.name = vals.name;
      payload.location = vals.location;
      await onSubmit(payload);
      toast.success(mode === "create" ? "Local creado" : "Local actualizado");
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuevo local" : "Editar local"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <Field className="col-span-2" label="Nombre">
            <Input value={vals.name} onChange={(e) => setVals({ ...vals, name: e.target.value })} />
          </Field>
          <Field className="col-span-2" label="Dirección">
            <Input value={vals.location} onChange={(e) => setVals({ ...vals, location: e.target.value })} />
          </Field>
          <Field label="Ciudad">
            <Input value={vals.city} onChange={(e) => setVals({ ...vals, city: e.target.value })} />
          </Field>
          <Field label="Estado">
            <Input value={vals.estado} onChange={(e) => setVals({ ...vals, estado: e.target.value })} />
          </Field>
          <Field label="Código postal">
            <Input value={vals.codigo_postal} onChange={(e) => setVals({ ...vals, codigo_postal: e.target.value })} />
          </Field>
          <Field label="Teléfono">
            <Input value={vals.phone} onChange={(e) => setVals({ ...vals, phone: e.target.value })} />
          </Field>
          <Field label="Abre (HH:MM)">
            <Input value={vals.opens_at} onChange={(e) => setVals({ ...vals, opens_at: e.target.value })} placeholder="07:00" />
          </Field>
          <Field label="Cierra (HH:MM)">
            <Input value={vals.closes_at} onChange={(e) => setVals({ ...vals, closes_at: e.target.value })} placeholder="20:00" />
          </Field>
          <Field className="col-span-2" label="Horarios (texto libre)">
            <Input value={vals.hours} onChange={(e) => setVals({ ...vals, hours: e.target.value })} />
          </Field>
          <Field className="col-span-2" label="Descuento miembros">
            <Input value={vals.member_discount} onChange={(e) => setVals({ ...vals, member_discount: e.target.value })} />
          </Field>
          <Field className="col-span-2" label="Descripción (max 120)">
            <Textarea
              maxLength={120}
              value={vals.description}
              onChange={(e) => setVals({ ...vals, description: e.target.value })}
            />
          </Field>
          <Field className="col-span-2" label="Google Maps URL">
            <Input value={vals.google_maps_url} onChange={(e) => setVals({ ...vals, google_maps_url: e.target.value })} />
          </Field>
          {mode === "create" && (
            <Field className="col-span-2" label="Propietario (rol Local)">
              <Select value={vals.owner_id} onValueChange={(v) => setVals({ ...vals, owner_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {cafeOwners.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.full_name ?? o.email ?? o.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* EVENTOS                                                             */
/* ------------------------------------------------------------------ */
function EventosTab({
  events,
  clubs,
  cafes,
  profiles,
  onExport,
}: {
  events: any[];
  clubs: any[];
  cafes: any[];
  profiles: any[];
  onExport: (rows: any[], name: string) => void;
}) {
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<any | null>(null);
  const refresh = useRefresh();
  const updateFn = useServerFn(adminUpdateEvent);
  const deleteFn = useServerFn(adminDeleteEvent);
  const createFn = useServerFn(adminCreateEvent);

  const exportRows = events.map((e) => ({
    Evento: e.name,
    Club: e.club_name,
    Local: e.cafe_name,
    Fecha: e.event_date,
    Hora: e.event_time,
    Distancia_km: e.distance_km ?? "",
    Inscritos: e.inscritos,
    Estado: e.estado_confirmacion,
  }));

  return (
    <div>
      <div className="flex justify-end gap-2 mb-3">
        <Button onClick={() => setCreating(true)} size="sm">
          <Plus className="w-4 h-4 mr-2" /> Nuevo evento
        </Button>
        <Button onClick={() => onExport(exportRows, "Eventos")} size="sm" variant="outline">
          <Download className="w-4 h-4 mr-2" /> Excel
        </Button>
      </div>
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Evento</TableHead>
              <TableHead>Club</TableHead>
              <TableHead>Local</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Hora</TableHead>
              <TableHead>Distancia</TableHead>
              <TableHead className="w-32">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{e.name}</TableCell>
                <TableCell>{e.club_name}</TableCell>
                <TableCell>{e.cafe_name}</TableCell>
                <TableCell>{e.event_date}</TableCell>
                <TableCell>{e.event_time}</TableCell>
                <TableCell>{e.distance_km ?? ""}</TableCell>
                <TableCell>
                  <RowActions onEdit={() => setEditing(e)} onDelete={() => setDeleting(e)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <EventFormDialog
          mode="edit"
          initial={editing}
          clubs={clubs}
          cafes={cafes}
          profiles={profiles}
          onClose={() => setEditing(null)}
          onSubmit={async (vals) => {
            await updateFn({ data: { id: editing.id, data: vals } });
          }}
          onDone={refresh}
        />
      )}
      {creating && (
        <EventFormDialog
          mode="create"
          initial={{}}
          clubs={clubs}
          cafes={cafes}
          profiles={profiles}
          onClose={() => setCreating(false)}
          onSubmit={async (vals) => {
            await createFn({ data: vals });
          }}
          onDone={refresh}
        />
      )}
      <ConfirmDelete
        open={!!deleting}
        title={`Eliminar evento ${deleting?.name ?? ""}`}
        description="Esta acción no se puede deshacer."
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await deleteFn({ data: { id: deleting.id } });
            toast.success("Evento eliminado");
            refresh();
          } catch (e: any) {
            toast.error(e?.message ?? "Error al eliminar");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </div>
  );
}

function EventFormDialog({
  mode,
  initial,
  clubs,
  cafes,
  profiles,
  onClose,
  onSubmit,
  onDone,
}: {
  mode: "create" | "edit";
  initial: any;
  clubs: any[];
  cafes: any[];
  profiles: any[];
  onClose: () => void;
  onSubmit: (vals: any) => Promise<void>;
  onDone: () => void;
}) {
  const [vals, setVals] = useState({
    name: initial.name ?? "",
    event_date: initial.event_date ?? "",
    event_time: initial.event_time ?? "",
    meeting_point: initial.meeting_point ?? "",
    meeting_details: initial.meeting_details ?? "",
    description: initial.description ?? "",
    distance_km: initial.distance_km != null ? String(initial.distance_km) : "",
    cupo_maximo: initial.cupo_maximo != null ? String(initial.cupo_maximo) : "",
    tipo_actividad: initial.tipo_actividad ?? "running",
    club_id: initial.club_id ?? "",
    cafe_id: initial.cafe_id ?? "",
    organizer_id: initial.organizer_id ?? profiles[0]?.id ?? "",
    cancelado: !!initial.cancelado,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const payload: any = {
        name: vals.name,
        event_date: vals.event_date,
        event_time: vals.event_time,
        meeting_point: vals.meeting_point,
        meeting_details: vals.meeting_details || null,
        description: vals.description || null,
        distance_km: vals.distance_km ? Number(vals.distance_km) : null,
        cupo_maximo: vals.cupo_maximo ? Number(vals.cupo_maximo) : null,
        tipo_actividad: vals.tipo_actividad,
        club_id: vals.club_id || null,
        cafe_id: vals.cafe_id || null,
        cancelado: vals.cancelado,
      };
      if (mode === "create") {
        // pick organizer: club owner > cafe owner > selected profile
        const club = clubs.find((c) => c.id === vals.club_id);
        const cafe = cafes.find((c) => c.id === vals.cafe_id);
        payload.organizer_id = club?.owner_id ?? cafe?.owner_id ?? vals.organizer_id;
        payload.organizador_tipo = vals.club_id ? "club" : "cafeteria";
      }
      await onSubmit(payload);
      toast.success(mode === "create" ? "Evento creado" : "Evento actualizado");
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuevo evento" : "Editar evento"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
          <Field className="col-span-2" label="Nombre">
            <Input value={vals.name} onChange={(e) => setVals({ ...vals, name: e.target.value })} />
          </Field>
          <Field label="Fecha">
            <Input type="date" value={vals.event_date} onChange={(e) => setVals({ ...vals, event_date: e.target.value })} />
          </Field>
          <Field label="Hora">
            <Input type="time" value={vals.event_time} onChange={(e) => setVals({ ...vals, event_time: e.target.value })} />
          </Field>
          <Field label="Club">
            <Select value={vals.club_id || "__none"} onValueChange={(v) => setVals({ ...vals, club_id: v === "__none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Ninguno —</SelectItem>
                {clubs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Café / Local">
            <Select value={vals.cafe_id || "__none"} onValueChange={(v) => setVals({ ...vals, cafe_id: v === "__none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Ninguno —</SelectItem>
                {cafes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tipo de actividad">
            <Select value={vals.tipo_actividad} onValueChange={(v) => setVals({ ...vals, tipo_actividad: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="ciclismo">Ciclismo</SelectItem>
                <SelectItem value="trail">Trail</SelectItem>
                <SelectItem value="caminata">Caminata</SelectItem>
                <SelectItem value="social">Social</SelectItem>
                <SelectItem value="ambos">Ambos</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Distancia (km)">
            <Input
              type="number"
              step="0.1"
              value={vals.distance_km}
              onChange={(e) => setVals({ ...vals, distance_km: e.target.value })}
            />
          </Field>
          <Field label="Cupo máximo">
            <Input
              type="number"
              value={vals.cupo_maximo}
              onChange={(e) => setVals({ ...vals, cupo_maximo: e.target.value })}
            />
          </Field>
          <Field className="col-span-2" label="Punto de encuentro">
            <Input value={vals.meeting_point} onChange={(e) => setVals({ ...vals, meeting_point: e.target.value })} />
          </Field>
          <Field className="col-span-2" label="Detalles del encuentro (max 150)">
            <Textarea
              maxLength={150}
              value={vals.meeting_details}
              onChange={(e) => setVals({ ...vals, meeting_details: e.target.value })}
            />
          </Field>
          <Field className="col-span-2" label="Descripción">
            <Textarea
              value={vals.description}
              onChange={(e) => setVals({ ...vals, description: e.target.value })}
            />
          </Field>
          {mode === "edit" && (
            <Field className="col-span-2" label="Cancelado">
              <Select value={vals.cancelado ? "yes" : "no"} onValueChange={(v) => setVals({ ...vals, cancelado: v === "yes" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Sí</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* INVITADOS (read-only)                                                */
/* ------------------------------------------------------------------ */
function InvitadosTab({
  invitados,
  onExport,
}: {
  invitados: any[];
  onExport: (rows: any[], name: string) => void;
}) {
  const rows = invitados.map((c) => ({
    Nombre: c.nombre,
    Telefono: c.telefono,
    Email: c.email ?? "",
    Direccion: c.direccion ?? "",
    Club: c.club_name ?? "",
    Fecha: c.fecha_invitacion,
    Estado: c.estado,
  }));
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => onExport(rows, "Cafes_invitados")} size="sm" variant="outline">
          <Download className="w-4 h-4 mr-2" /> Descargar Excel
        </Button>
      </div>
      <DataTable
        headers={["Nombre", "Teléfono", "Email", "Dirección", "Club", "Fecha", "Estado"]}
        rows={rows}
      />
    </div>
  );
}

function DataTable({ headers, rows }: { headers: string[]; rows: Record<string, any>[] }) {
  if (rows.length === 0) {
    return <p className="text-muted-foreground p-6 text-center border rounded-md">Sin datos.</p>;
  }
  const keys = Object.keys(rows[0]);
  return (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {headers.map((h) => (
              <TableHead key={h}>{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {keys.map((k) => (
                <TableCell key={k}>{String(r[k] ?? "")}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shared bits                                                          */
/* ------------------------------------------------------------------ */
function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="flex gap-1">
      <Button size="icon" variant="ghost" onClick={onEdit} title="Editar">
        <Pencil className="w-4 h-4" />
      </Button>
      <Button size="icon" variant="ghost" onClick={onDelete} title="Eliminar" className="text-destructive">
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function ConfirmDelete({
  open,
  title,
  description,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Eliminar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PendientesTab({ cafes, clubs }: { cafes: any[]; clubs: any[] }) {
  const qc = useQueryClient();
  const setStatus = useServerFn(adminSetBusinessStatus);
  const [busy, setBusy] = useState<string | null>(null);

  const items = useMemo(() => {
    const a = (cafes ?? [])
      .filter((c) => c.status === "pending")
      .map((c) => ({ kind: "cafe" as const, raw: c }));
    const b = (clubs ?? [])
      .filter((c) => c.status === "pending")
      .map((c) => ({ kind: "club" as const, raw: c }));
    return [...a, ...b].sort(
      (x, y) => new Date(y.raw.created_at).getTime() - new Date(x.raw.created_at).getTime(),
    );
  }, [cafes, clubs]);

  const act = async (kind: "cafe" | "club", id: string, status: "approved" | "rejected") => {
    setBusy(id);
    try {
      await setStatus({ data: { kind, id, status } });
      toast.success(status === "approved" ? "Negocio aprobado" : "Negocio rechazado");
      await qc.invalidateQueries({ queryKey: ["admin-data"] });
    } catch (e: any) {
      toast.error(e?.message ?? "No se pudo actualizar");
    } finally {
      setBusy(null);
    }
  };

  if (items.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        No hay negocios pendientes de aprobación.
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl">Negocios pendientes de aprobación</h2>
      {items.map(({ kind, raw }) => (
        <Card key={raw.id} className="p-4 md:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{kind === "cafe" ? "Café" : "Club"}</Badge>
                <span className="font-semibold">{raw.name}</span>
                <span className="text-sm text-muted-foreground">{raw.city ?? "—"}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Dueño: {raw.owner_name || raw.organizer_name || "—"} · Creado:{" "}
                {new Date(raw.created_at).toLocaleDateString("es-MX")}
              </p>
              {kind === "cafe" ? (
                <p className="text-sm text-muted-foreground">
                  Dirección: {raw.location || "—"} · Teléfono: {raw.phone || "—"}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Tipo: {raw.type || "—"} · Instagram: {raw.instagram || "—"}
                </p>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                size="sm"
                disabled={busy === raw.id}
                onClick={() => act(kind, raw.id, "approved")}
              >
                <Check className="mr-1 h-4 w-4" /> Aprobar
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy === raw.id}
                onClick={() => act(kind, raw.id, "rejected")}
              >
                <X className="mr-1 h-4 w-4" /> Rechazar
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
